// src/auth/ProtectedRoute.jsx
// FASE 1.B — Guard multi-nivel: requireAuth, requireRole, requireAccess.
// Admin pasa siempre el guard requireAccess (admin no necesita suscripción).
//
// UX audit fixes:
//  - ISSUE 4: chequea emailVerified ANTES de redirigir a /paywall, para
//    que el flujo natural sea signup → verificar email → paywall, no
//    loop /paywall ↔ /dashboard mientras el email no está verificado.
//  - ISSUE 5: falta de rol redirige a /sin-permisos (no silencioso).
//  - ISSUE 10: detecta transición de sesión activa → null como expiración
//    y redirige a /login?reason=session_expired para mostrar banner.
import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isPausedFor } from '../config/servicePaused';
import ServicePaused from '../pages/ServicePaused';

export default function ProtectedRoute({
  children,
  requireAuth = true,
  requireRole = null,
  requireAccess = false,
}) {
  const { user, loading, isAdmin, hasActiveAccess, emailVerified,
          userDataReady, userDocError } = useAuth();
  const location = useLocation();

  // ISSUE 10: marca que hubo sesión activa para detectar expiración.
  // Si después user pasa a null, sabemos que fue por expiración (no
  // primer load sin login). Persistimos en sessionStorage para que
  // sobreviva re-renders / navegaciones cruzadas dentro de la misma tab.
  const hadSessionRef = useRef(false);
  useEffect(() => {
    if (user) {
      hadSessionRef.current = true;
      try { sessionStorage.setItem('ee_had_session', '1'); } catch { /* ignore */ }
    }
  }, [user]);

  // Nunca decidir el paywall a ciegas: si hay sesión pero el doc de usuario
  // todavía no llegó (o falló su lectura), mostramos carga. Antes, un error
  // momentáneo de Firestore dejaba loading=false con userData=null y
  // hasActiveAccess=false → cliente con acceso vigente al paywall
  // (intermitente, se "arreglaba" recargando).
  if (loading || (user && !userDataReady)) {
    return (
      <div className="mobile-root">
        <div
          className="screen"
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
          }}
        >
          {user && !userDataReady && userDocError
            ? 'Reconectando…'
            : 'Cargando...'}
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    // ISSUE 10: si tuvo sesión activa antes y ahora la perdió → expirada.
    let hadSession = hadSessionRef.current;
    try { hadSession = hadSession || sessionStorage.getItem('ee_had_session') === '1'; } catch { /* ignore */ }
    if (hadSession) {
      try { sessionStorage.removeItem('ee_had_session'); } catch { /* ignore */ }
      return <Navigate to="/login?reason=session_expired" state={{ from: location }} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireRole === 'admin' && !isAdmin) {
    // ISSUE 5: redirect explícito a página con explicación, no silencioso.
    return <Navigate to="/sin-permisos" replace />;
  }

  // Pausa del servicio (21-sep-2026). Va acá, después de resolver la
  // identidad y antes de cualquier decisión sobre acceso o paywall: así un
  // cliente con sesión no llega al dashboard ni al checkout, y tampoco
  // rebota a /paywall, que también está cerrado. El admin no entra nunca
  // en esta rama.
  // Se muestra la pantalla en el lugar en vez de redirigir, para no
  // depender de una ruta que a su vez habría que proteger.
  if (isPausedFor(isAdmin)) {
    return <ServicePaused />;
  }

  // ISSUE 4: si requiere acceso, primero validar email. Sin email
  // verificado el usuario debería verificar antes de pagar/usar dashboard.
  // Admin siempre pasa este chequeo (no necesita verificar email).
  if (requireAccess && !isAdmin && user && !emailVerified) {
    return <Navigate to="/verificar-email" replace />;
  }

  // FIX paywall bypass: usamos hasActiveAccess (estricto) en vez de
  // hasAccess (que tiene fallback legacy `pagoActivo`). Cliente solo
  // entra al dashboard si tiene accessUntil futuro. Admin pasa siempre.
  if (requireAccess && !hasActiveAccess && !isAdmin) {
    return <Navigate to="/paywall" replace />;
  }

  return children;
}
